"use client";

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

export interface CentreData {
  id: string;
  slug: string;
  nom: string;
  adresse: string;
  commune: string;
  telephone: string;
  hours: string;
  hoursWeekend: string;
  specialties: string[];
}

interface Props {
  centres: CentreData[];
}

export default function CentersSection({ centres }: Props) {
  const sectionRef = useRef<HTMLDivElement>(null);



  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll('.center-card-anim');
            cards.forEach((card, i) => {
              setTimeout(() => {
                card.classList.add('reveal-visible');
                card.classList.remove('reveal-hidden');
              }, i * 150);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="centres" className="py-20 md:py-28 bg-bg-gray" aria-labelledby="centers-title">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-primary text-sm font-semibold uppercase tracking-widest mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Notre réseau
          </p>
          <h2
            id="centers-title"
            className="text-3xl md:text-4xl font-semibold text-text-main mb-3"
            style={{ fontFamily: 'Poppins, sans-serif', letterSpacing: '-0.01em' }}
          >
            Trouvez le centre le plus proche
          </h2>
          <span className="section-decorator" aria-hidden="true" />
          <p className="text-text-muted text-base leading-relaxed max-w-2xl mx-auto mt-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Nos centres sont répartis à Bruxelles et en Brabant flamand pour vous garantir un accès facile aux soins au tarif conventionné, près de chez vous.
          </p>
        </div>

        {/* Centers list */}
        <div className="space-y-6" ref={sectionRef}>
          {centres.map((centre, index) => (
            <div
              key={centre.id}
              className={`center-card-anim reveal-hidden bg-white rounded-card shadow-card card-hover border border-border overflow-hidden delay-${(index + 1) * 100}`}
              role="article"
              aria-label={`Centre médical : ${centre.nom}`}
            >
              <div className="flex flex-col md:flex-row">
                {/* Left accent */}
                <div className="w-full md:w-1.5 h-1.5 md:h-auto bg-primary flex-shrink-0" aria-hidden="true" />

                {/* Content */}
                <div className="flex-1 p-6 md:p-8">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                    {/* Name + address */}
                    <div className="flex-1">
                      <h3
                        className="text-xl font-semibold text-text-main mb-2"
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                      >
                        {centre.nom}
                      </h3>
                      <div className="flex items-start gap-2 text-text-muted text-sm mb-4">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#DB052C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 flex-shrink-0" aria-hidden="true">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                          <circle cx="12" cy="10" r="3"/>
                        </svg>
                        <span style={{ fontFamily: 'Poppins, sans-serif' }}>{centre.adresse}</span>
                      </div>
                      {centre.specialties.length > 0 && (
                        <div className="flex flex-wrap gap-2" role="list" aria-label="Spécialités disponibles">
                          {centre.specialties.map((spec) => (
                            <span key={spec} className="badge-soft" role="listitem">{spec}</span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Hours + Phone */}
                    <div className="flex flex-col sm:flex-row lg:flex-col gap-4 lg:gap-3 lg:min-w-48">
                      {(centre.hours || centre.hoursWeekend) && (
                        <div className="flex items-start gap-2">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#DB052C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 flex-shrink-0" aria-hidden="true">
                            <circle cx="12" cy="12" r="10"/>
                            <polyline points="12 6 12 12 16 14"/>
                          </svg>
                          <div>
                            {centre.hours && <p className="text-text-main text-sm font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>{centre.hours}</p>}
                            {centre.hoursWeekend && <p className="text-text-muted text-xs" style={{ fontFamily: 'Poppins, sans-serif' }}>{centre.hoursWeekend}</p>}
                          </div>
                        </div>
                      )}
                      {centre.telephone && (
                        <div className="flex items-center gap-2">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#DB052C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0" aria-hidden="true">
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                          </svg>
                          <a
                            href={`tel:${centre.telephone.replace(/\s/g, '')}`}
                            className="text-text-main text-sm font-medium hover:text-primary transition-colors"
                            style={{ fontFamily: 'Poppins, sans-serif' }}
                            aria-label={`Appeler ${centre.nom} au ${centre.telephone}`}
                          >
                            {centre.telephone}
                          </a>
                        </div>
                      )}
                    </div>

                    {/* CTA */}
                    <div className="flex-shrink-0">
                      <Link
                        href={`/centres/${centre.slug}`}
                        className="btn-primary text-sm"
                        aria-label={`Voir le centre ${centre.nom}`}
                      >
                        Voir le centre
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M5 12h14M12 5l7 7-7 7"/>
                        </svg>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* See all */}
        <div className="text-center mt-10">
          <Link href="/centres" className="btn-secondary text-sm" aria-label="Voir tous les centres médicaux CMMC CDP">
            Voir tous nos centres
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
}
